import { Stack, Text, Group, Box } from '@mantine/core';
import type { DesignedShift } from '../types/prediction-types';

interface ShiftTimelineProps {
  shifts: DesignedShift[];
  totalRevenue: number;
  isCompact?: boolean;
}

interface TimeSegment {
  startMinutes: number;
  endMinutes: number;
  employeeCount: number;
}

function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const period = hours >= 12 ? 'pm' : 'am';
  const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
  
  // Show minutes if not on the hour
  if (mins === 0) {
    return `${displayHours}${period}`;
  } else {
    return `${displayHours}:${mins.toString().padStart(2, '0')}${period}`;
  }
}

export default function ShiftTimeline({ shifts, totalRevenue, isCompact = false }: ShiftTimelineProps) {
  if (shifts.length === 0) {
    return (
      <Text size="xs" c="dimmed">
        No shifts
      </Text>
    );
  }

  // Calculate coverage segments with employee counts
  const allTimes: { time: number; delta: number }[] = [];
  
  shifts.forEach(shift => {
    const start = timeToMinutes(shift.start_time);
    const end = timeToMinutes(shift.end_time);
    allTimes.push({ time: start, delta: 1 });
    allTimes.push({ time: end, delta: -1 });
  });

  allTimes.sort((a, b) => a.time - b.time);

  const segments: TimeSegment[] = [];
  let currentEmployees = 0;
  let lastTime = allTimes[0].time;

  allTimes.forEach(({ time, delta }) => {
    if (time > lastTime && currentEmployees > 0) {
      segments.push({
        startMinutes: lastTime,
        endMinutes: time,
        employeeCount: currentEmployees,
      });
    }
    currentEmployees += delta;
    lastTime = time;
  });

  // Find min/max times for scaling
  const minTime = Math.min(...segments.map(s => s.startMinutes));
  const maxTime = Math.max(...segments.map(s => s.endMinutes));
  const totalMinutes = maxTime - minTime;
  const maxEmployees = Math.max(...segments.map(s => s.employeeCount));

  if (isCompact) {
    // Compact view for calendar cells
    return (
      <Stack gap={2}>
        <Box style={{ position: 'relative', height: '40px', width: '100%' }}>
          {/* Timeline background */}
          <Box
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '100%',
              background: '#f1f3f5',
              borderRadius: '4px',
            }}
          />
          
          {/* Coverage segments */}
          {segments.map((segment, idx) => {
            const leftPercent = ((segment.startMinutes - minTime) / totalMinutes) * 100;
            const widthPercent = ((segment.endMinutes - segment.startMinutes) / totalMinutes) * 100;
            const isOverlap = segment.employeeCount > 1;
            
            return (
              <Box
                key={idx}
                style={{
                  position: 'absolute',
                  left: `${leftPercent}%`,
                  width: `${widthPercent}%`,
                  top: '8px',
                  height: '24px',
                  background: isOverlap ? '#fa5252' : '#339af0',
                  opacity: 0.8,
                  borderRadius: '2px',
                  border: '1px solid #fff',
                }}
                title={`${minutesToTime(segment.startMinutes)}-${minutesToTime(segment.endMinutes)} (${segment.employeeCount} emp)`}
              />
            );
          })}
        </Box>
        
        {/* Time labels */}
        <Group gap={4} justify="space-between">
          <Text size="9px" c="dimmed">{minutesToTime(minTime)}</Text>
          <Text size="9px" fw={600}>${totalRevenue.toFixed(0)}</Text>
          <Text size="9px" c="dimmed">{minutesToTime(maxTime)}</Text>
        </Group>
        
        {maxEmployees > 1 && (
          <Text size="8px" c="red" fw={500}>
            Max: {maxEmployees} employees
          </Text>
        )}
      </Stack>
    );
  }

  // Full view for expanded details
  // Calculate time range from actual shifts ONCE
  const shiftTimes = shifts.flatMap(s => [
    timeToMinutes(s.start_time),
    timeToMinutes(s.end_time)
  ]);
  const shiftMinTime = Math.min(...shiftTimes);
  const shiftMaxTime = Math.max(...shiftTimes);
  const shiftTotalMinutes = shiftMaxTime - shiftMinTime;

  return (
    <Stack gap="xs">
      <Text size="sm" fw={600}>Shift Timeline</Text>
      
      {/* Vertical Gantt Timeline */}
      <Box>
        {/* Time axis at top */}
        <Group gap="xs" wrap="nowrap" style={{ marginBottom: '8px' }}>
          {/* Spacer to match time label width */}
          <Box style={{ minWidth: '120px', flexShrink: 0 }} />
          
          {/* Time axis container */}
          <Box style={{ position: 'relative', height: '30px', flex: 1 }}>
            {/* Time axis line */}
            <Box
              style={{
                position: 'absolute',
                top: '20px',
                left: 0,
                right: 0,
                height: '1px',
                background: '#dee2e6',
              }}
            />
            
            {/* Time labels */}
            {(() => {
              const labels: { time: number; label: string }[] = [];
              const startHour = Math.floor(shiftMinTime / 60);
              const endHour = Math.ceil(shiftMaxTime / 60);
              
              // Always include the actual start time
              labels.push({
                time: shiftMinTime,
                label: minutesToTime(shiftMinTime)
              });
              
              // Generate time labels every 3 hours
              for (let hour = Math.ceil(startHour / 3) * 3; hour <= endHour; hour += 3) {
                const timeMinutes = hour * 60;
                if (timeMinutes > shiftMinTime && timeMinutes < shiftMaxTime) {
                  labels.push({
                    time: timeMinutes,
                    label: minutesToTime(timeMinutes)
                  });
                }
              }
              
              // Always include the actual end time (if different from last label)
              if (labels[labels.length - 1].time !== shiftMaxTime) {
                labels.push({
                  time: shiftMaxTime,
                  label: minutesToTime(shiftMaxTime)
                });
              }
              
              return labels.map((label, idx) => {
                const position = ((label.time - shiftMinTime) / shiftTotalMinutes) * 100;
                const isFirst = idx === 0;
                const isLast = idx === labels.length - 1;
                
                return (
                  <Box
                    key={label.time}
                    style={{
                      position: 'absolute',
                      left: `${position}%`,
                      top: '0px',
                      transform: isFirst ? 'translateX(0%)' : isLast ? 'translateX(-100%)' : 'translateX(-50%)',
                    }}
                  >
                    <Text size="10px" c="dimmed" fw={500}>
                      {label.label}
                    </Text>
                    {/* Tick mark */}
                    <Box
                      style={{
                        position: 'absolute',
                        left: isFirst ? '0%' : isLast ? '100%' : '50%',
                        top: '15px',
                        width: '1px',
                        height: '5px',
                        background: '#dee2e6',
                      }}
                    />
                  </Box>
                );
              });
            })()}
          </Box>
          
          {/* Spacer to match duration width */}
          <Box style={{ minWidth: '60px', flexShrink: 0 }} />
        </Group>
        
        {/* Shifts as horizontal bars */}
        <Stack gap={4}>
          {shifts.map((shift, idx) => {
            const startMinutes = timeToMinutes(shift.start_time);
            const endMinutes = timeToMinutes(shift.end_time);
            const leftPercent = ((startMinutes - shiftMinTime) / shiftTotalMinutes) * 100;
            const widthPercent = ((endMinutes - startMinutes) / shiftTotalMinutes) * 100;
            
            // Calculate duration
            const durationMinutes = endMinutes - startMinutes;
            const durationHours = Math.floor(durationMinutes / 60);
            const durationMins = durationMinutes % 60;
            const durationText = durationMins > 0 
              ? `${durationHours}h ${durationMins}m`
              : `${durationHours}h`;
            
            return (
              <Group key={idx} gap="xs" wrap="nowrap">
                {/* Time range label */}
                <Text size="xs" style={{ minWidth: '120px', flexShrink: 0 }}>
                  {shift.start_time}-{shift.end_time}
                </Text>
                
                {/* Bar container */}
                <Box style={{ position: 'relative', flex: 1, height: '24px' }}>
                  {/* Background track */}
                  <Box
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      height: '2px',
                      background: '#e9ecef',
                    }}
                  />
                  
                  {/* Shift bar */}
                  <Box
                    style={{
                      position: 'absolute',
                      left: `${leftPercent}%`,
                      width: `${widthPercent}%`,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      height: '20px',
                      background: '#339af0',
                      borderRadius: '4px',
                      border: '1px solid #fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Text size="9px" c="white" fw={600}>
                      {shift.employees} emp
                    </Text>
                  </Box>
                </Box>
                
                {/* Duration */}
                <Text size="xs" c="dimmed" style={{ minWidth: '60px', flexShrink: 0 }}>
                  {durationText}
                </Text>
              </Group>
            );
          })}
        </Stack>
      </Box>
      
      <Text size="sm" fw={600} mt="xs">
        Total Revenue: ${totalRevenue.toFixed(2)}
      </Text>
    </Stack>
  );
}
